import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";

const roles = ["Admin", "Accounts", "Service Manager", "Engineer", "Store", "Sales"];
const permissionKeys = [
  { id: "view", label: "View / Read" },
  { id: "create", label: "Create / Add" },
  { id: "edit", label: "Edit / Update" },
  { id: "delete", label: "Delete / Purge" },
  { id: "approve", label: "Approve Documents" },
  { id: "export", label: "Export CSV / Report" },
  { id: "print", label: "Print Documents" }
];

export default function RolesPermissions() {
  const [loading, setLoading] = useState(true);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [savingRole, setSavingRole] = useState("");

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/admin-settings/role-permissions", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setRolePermissions(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      alert("Failed to load roles and permissions matrix.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (roleName, permId, currentVal) => {
    try {
      setSavingRole(roleName);
      const permissionsObj = rolePermissions.find(rp => rp.roleName === roleName)?.permissions || {};
      const updatedPermissions = {
        ...permissionsObj,
        [permId]: !currentVal
      };

      const res = await axios.put(API_BASE_URL + "/api/admin-settings/role-permissions", {
        roleName,
        permissions: updatedPermissions
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });

      setRolePermissions(curr =>
        curr.map(item => item.roleName === roleName ? res.data : item)
      );
    } catch (error) {
      console.error(error);
      alert("Failed to update role permissions.");
    } finally {
      setSavingRole("");
    }
  };

  const getPermVal = (roleName, permId) => {
    const rolePerm = rolePermissions.find(rp => rp.roleName === roleName);
    return rolePerm?.permissions?.[permId] || false;
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-500 font-semibold">Loading access permissions matrix...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="border-b pb-4">
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Roles &amp; Permissions</h1>
        <p className="text-sm text-slate-500 mt-1">Configure workspace operations and data access permissions mapping for employee roles.</p>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="py-4 px-4 w-48">System Role</th>
                {permissionKeys.map(pk => (
                  <th key={pk.id} className="py-4 px-4 text-center">{pk.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-250 dark:divide-slate-700">
              {roles.map(role => (
                <tr key={role} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition">
                  <td className="py-4 px-4 font-bold text-slate-800 dark:text-slate-300 flex items-center gap-2">
                    <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-lg">admin_panel_settings</span>
                    <span>{role}</span>
                    {savingRole === role && (
                      <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded font-normal">Saving...</span>
                    )}
                  </td>
                  {permissionKeys.map(pk => {
                    const isChecked = getPermVal(role, pk.id);
                    return (
                      <td key={pk.id} className="py-4 px-4 text-center">
                        <label className="inline-flex items-center cursor-pointer justify-center">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggle(role, pk.id, isChecked)}
                            disabled={role === "Admin" && pk.id === "view"} // Admin view cannot be disabled
                            className="w-5 h-5 text-blue-600 border-slate-300 dark:border-slate-600 rounded focus:ring-blue-500 disabled:opacity-40"
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-300 dark:border-amber-900/30 rounded-2xl p-5 flex items-start gap-4">
        <span className="material-symbols-outlined text-amber-600 dark:text-amber-400">info</span>
        <div className="text-xs text-amber-800 dark:text-amber-300 space-y-1">
          <p className="font-bold">Security Note</p>
          <p>Role updates apply instantly. If an employee with a role performs an API action, permissions check verifies the rules defined above. View permission is minimum requirement to navigate pages.</p>
        </div>
      </div>
    </div>
  );
}
